import OrganizationLiveToggle from "./OrganizationLiveToggle";
import { useListState } from "../../lib/listState";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AlertCircle, Building2, FolderKanban, MapPin, Search } from "lucide-react";
import { api } from "../../lib/api";
import type { TechOpsOrganizationRow } from "../../lib/types";
import { formatDateTime, formatNumber, labelFromCode, statusTone } from "../shared/format";
import { RouteError, RouteLoading } from "../shared/PageState";

function StatTile({ label, value, detail }: { label: string; value: number; detail: string }) {
  return (
    <div className="pa-stat-tile">
      <small>{label}</small>
      <strong>{formatNumber(value)}</strong>
      <span>{detail}</span>
    </div>
  );
}

function OrganizationRows({ organizations, canApprove }: { organizations: TechOpsOrganizationRow[]; canApprove: boolean }) {
  if (organizations.length === 0) {
    return (
      <tr>
        <td colSpan={8} className="pa-empty-cell">No organizations match the current filters.</td>
      </tr>
    );
  }
  return organizations.map((organization) => (
    <tr key={organization.organization_id}>
      <td>
        <Link className="pa-detail-link" to={`/organizations/${organization.organization_id}`}>{organization.organization_name}</Link>
        <small>{organization.organization_id}</small>
      </td>
      <td>{organization.organization_type === "SMB" ? "SMB" : labelFromCode(organization.organization_type)}</td>
      <td><span className={`pa-badge ${statusTone(organization.status)}`}>{organization.status}</span></td>
      <td><OrganizationLiveToggle organizationId={organization.organization_id} name={organization.organization_name} live={organization.is_live} canApprove={canApprove} /></td>
      <td>{formatNumber(organization.active_project_count)} / {formatNumber(organization.total_project_count)}</td>
      <td>{formatNumber(organization.location_count)}</td>
      <td>{formatNumber(organization.catalogue_item_count)}</td>
      <td>
        <strong>{formatNumber(organization.pending_approval_count)}</strong>
        <small>{organization.failed_import_count > 0 ? `${formatNumber(organization.failed_import_count)} failed imports` : "No failed imports"}</small>
      </td>
    </tr>
  ));
}

export default function OrganizationsPage() {
  const [search, setSearch] = useListState("organizations.search", "");
  const [status, setStatus] = useListState("organizations.status", "ALL");
  const dashboard = useQuery({ queryKey: ["tech-ops-dashboard"], queryFn: api.getDashboard });
  const organizationsQuery = useQuery({
    queryKey: ["tech-ops-organizations", search, status],
    queryFn: () => api.listOrganizations({ search, status }),
    placeholderData: keepPreviousData
  });

  if (organizationsQuery.isLoading) {
    return <RouteLoading />;
  }

  if (organizationsQuery.isError || !organizationsQuery.data) {
    return (
      <RouteError
        title="Organizations unavailable"
        detail="The organization directory could not be loaded from the Coprint backend."
        onRetry={() => organizationsQuery.refetch()}
      />
    );
  }

  const { summary, organizations, generated_at: generatedAt } = organizationsQuery.data;

  return (
    <div className="pa-page-stack">
      <div className="pa-title-row">
        <div>
          <p className="pa-eyebrow">Organizations</p>
          <h1>Organization directory</h1>
          <p>Select an organization to review its details. Enable Live after approval to grant workspace and import access.</p>
        </div>
        <div className="pa-detail-actions">
          <span className="pa-refresh-chip">Updated {formatDateTime(generatedAt)}</span>
          {dashboard.data?.platform_user.roles.includes("TECH_ADMIN") && <Link className="pa-primary-button" to="/organizations/new"><Building2 size={17} /> Create organization</Link>}
        </div>
      </div>

      <section className="pa-stat-grid four">
        <StatTile label="Total" value={summary.total_organizations} detail="Organizations" />
        <StatTile label="Active" value={summary.active_organizations} detail="Active status" />
        <StatTile label="Suspended" value={summary.suspended_organizations} detail="Paused tenants" />
        <StatTile label="Disabled" value={summary.disabled_organizations} detail="Inactive tenants" />
      </section>

      <section className="pa-panel">
        <div className="pa-toolbar">
          <label className="pa-search-field">
            <Search size={16} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search organizations" />
          </label>
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="ALL">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="SUSPENDED">Suspended</option>
            <option value="DISABLED">Disabled</option>
          </select>

        </div>
        <div className="pa-strip-metrics">
          <span><FolderKanban size={15} /> {formatNumber(organizations.reduce((sum, org) => sum + org.total_project_count, 0))} listed projects</span>
          <span><MapPin size={15} /> {formatNumber(organizations.reduce((sum, org) => sum + org.location_count, 0))} listed locations</span>
          <span><AlertCircle size={15} /> {formatNumber(organizations.reduce((sum, org) => sum + org.pending_approval_count, 0))} pending approvals</span>
        </div>
        <div className="pa-table-scroll">
          <table className="pa-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Type</th>
                <th>Status</th>
                <th>Live</th>
                <th>Projects</th>
                <th>Locations</th>
                <th>Catalogue</th>
                <th>Attention</th>
              </tr>
            </thead>
            <tbody>
              <OrganizationRows organizations={organizations} canApprove={!!dashboard.data?.platform_user.roles.some(role => role === "TECH_ADMIN" || role === "TECH_OPS")} />
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
